import { isEqual } from 'lodash'
import Maybe from 'folktale/maybe'
import {
  WRITE,
  USER_INPUT_FILTER_V1_ROLE,
} from '@wix/wix-data-client-common-standalone'
import { Dataset } from '../../business-logic/datasets/DatasetWithRelationToAncestorRepeater'
import { uouFilterSelectFilteringField } from '@wix/bi-logger-platform/v2'
import getSectionByRole from './state/component/getSectionByRole'

const getDataset = (datasets, selectedDataSource) =>
  selectedDataSource.chain(({ controllerRef }) =>
    Maybe.fromNullable(
      datasets.find(dataset => isEqual(dataset.controllerRef, controllerRef)),
    ),
  )

const getCollection = (collections, collectionId) =>
  Maybe.fromNullable(
    collections.find(collection => collection.id === collectionId),
  )

const getCollectionByDataSource = (collections, datasets, selectedDataSource) =>
  getDataset(datasets, selectedDataSource).chain(dataset =>
    getCollection(collections, dataset.config.collectionId),
  )

const getCollectionByDataset = (collections, dataset) =>
  getCollection(collections, dataset.config.collectionId)

const getUserFilterSection = component =>
  component.sections.find(section => section.isUserInputFilter)

const getUserInputFilterConnection = ({ bindingDefinitions, connections }) => {
  const filterSection = bindingDefinitions?.sections.find(
    section => section.isUserInputFilter,
  )

  return connections.find(({ role }) => role === filterSection?.role)
}

const disableWriteModeDatasets = datasets =>
  datasets.map(d =>
    d.matchWith({
      Regular: dataset =>
        Dataset.Regular({
          ...dataset,
          disabled: dataset?.config.readWriteMode === WRITE,
        }),
      Primary: dataset =>
        Dataset.Primary({
          ...dataset,
          disabled: dataset?.config.readWriteMode === WRITE,
        }),
      Details: dataset =>
        Dataset.Details({
          ...dataset,
          disabled: dataset?.config.readWriteMode === WRITE,
        }),
      Other: dataset =>
        Dataset.Other({
          ...dataset,
          disabled: dataset?.config.readWriteMode === WRITE,
        }),
    }),
  )

const shouldHideBannerForV1Filter = component => {
  const filterV1Section = getSectionByRole(component, USER_INPUT_FILTER_V1_ROLE)
  const componentIsUsedAsV1Filter =
    filterV1Section && Maybe.Just.hasInstance(filterV1Section.dataSource)
  return componentIsUsedAsV1Filter
}

const shouldShowNotificationBanner = component => {
  return (
    getUserFilterSection(component) &&
    !Maybe.Just.hasInstance(component.repeaterAncestor) &&
    !shouldHideBannerForV1Filter(component)
  )
}

const sendUOUFilterSelectFilteringFieldBiEvent = async ({
  logger,
  editorSdkProxy,
  component,
  collection,
  datasets,
  section,
  fieldKey,
  fieldType,
}) => {
  const pageRef = await editorSdkProxy.components.getPage({
    componentRef: component.data.ref,
  })
  const { title } = await editorSdkProxy.pages.getPageData({ pageRef })
  const selectedDataset = getDataset(datasets, section.dataSource)

  logger.bi(
    uouFilterSelectFilteringField({
      collection_id: collection.id,
      collection_name: collection.displayName,
      component_type: component.data.type,
      dataset_name: selectedDataset.value?.displayName,
      pageId: pageRef.id,
      page_name: title,
      field_name: fieldKey,
      field_type: fieldType,
    }),
  )
}

const isCollectionWritable = collection => {
  const { isTPACollection, isAppCollection } = collection.getOrElse({})
  return !isTPACollection && !isAppCollection
}

export {
  getDataset,
  getCollection,
  getCollectionByDataSource,
  getCollectionByDataset,
  getUserFilterSection,
  getUserInputFilterConnection,
  disableWriteModeDatasets,
  shouldShowNotificationBanner,
  sendUOUFilterSelectFilteringFieldBiEvent,
  isCollectionWritable,
}
